import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import DeleteOrganizationModal from '@/components/DeleteOrganizationModal';
import EditOrganizationModal from '@/components/EditOrganizationModal';
import { DataTable, TableActionBar, type DataTableColumn } from '@/components/table';
import { AppMenu } from '@/components/ui';
import type { AppMenuItem } from '@/components/ui';
import { organizationsService, type Organization } from '@/api/services/organizations';
import { useWorkspace } from '@/context/WorkspaceContext';
import { workspaceIcon } from '@/utils/workspaceIcons';
import { formatApiError } from '@/utils/formatApiError';
import { useToast } from '@/components/Toast';
import {
  MenuArchiveIcon,
  MenuCreditCardIcon,
  MenuPencilIcon,
  MenuTrashIcon,
} from '@/ui/icons/MenuIcons';

type OrganizationRowActionsProps = {
  org: Organization;
  onEdit: () => void;
  onDelete: () => void;
  onOpen: () => void;
};

function OrganizationRowActions({ org, onEdit, onDelete, onOpen }: OrganizationRowActionsProps) {
  const items = useMemo<AppMenuItem[]>(() => {
    const menuItems: AppMenuItem[] = [
      {
        id: 'edit',
        label: 'Edit',
        icon: <MenuPencilIcon />,
        onSelect: onEdit,
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: <MenuCreditCardIcon />,
        href: `/admin/billing/organizations/${org.id}`,
      },
      {
        id: 'archive',
        label: 'Archive',
        icon: <MenuArchiveIcon />,
        disabled: true,
      },
      {
        id: 'delete',
        label: 'Delete organization',
        icon: <MenuTrashIcon />,
        destructive: true,
        onSelect: onDelete,
      },
    ];
    return menuItems;
  }, [onDelete, onEdit, org.id]);

  return (
    <TableActionBar>
      <button type="button" className="btn btn-sm btn-primary" onClick={onOpen}>
        Open workspace
      </button>
      <AppMenu
        label={`Actions for ${org.name}`}
        items={items}
      />
    </TableActionBar>
  );
}

export default function PlatformOrganizationsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectWorkspace, canManageOrganizations, activeOrganizationId, removeWorkspaceFromList, invalidateWorkspaces } = useWorkspace();

  const [items, setItems] = useState<Organization[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(searchParams.get('create') === '1');
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [deleteOrg, setDeleteOrg] = useState<Organization | null>(null);

  const load = useCallback(async (term?: string) => {
    setLoading(true);
    try {
      const res = await organizationsService.platformList(term);
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageOrganizations) {
      setError('You do not have permission to manage organizations.');
      setLoading(false);
      return;
    }
    void load(search);
  }, [canManageOrganizations, load, search]);

  async function createOrganization(event: React.FormEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      const org = await organizationsService.platformCreate({ name });
      toast(`Created ${org.name}`, 'success');
      setNewName('');
      setCreating(false);
      setSearchParams({});
      await load(search);
      await selectWorkspace(org.id);
      navigate('/workspace');
    } catch (e) {
      toast(formatApiError(e), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function openOrganization(org: Organization) {
    try {
      await selectWorkspace(org.id);
      navigate('/workspace');
    } catch (e) {
      toast(formatApiError(e), 'error');
    }
  }

  async function saveOrganizationName(name: string) {
    if (!editOrg) return;
    const updated = await organizationsService.platformUpdate(editOrg.id, { name });
    toast(`Updated ${updated.name}`, 'success');
    setEditOrg(null);
    await load(search);
  }

  async function deleteOrganization(confirmationName: string) {
    if (!deleteOrg) return;
    const deletedId = deleteOrg.id;
    const deletedName = deleteOrg.name;
    const wasActive = activeOrganizationId === deletedId;
    await organizationsService.platformDelete(deletedId, confirmationName);
    removeWorkspaceFromList(deletedId);
    if (wasActive) {
      await selectWorkspace(null);
    } else {
      await invalidateWorkspaces();
    }
    toast(`Deleted ${deletedName}`, 'success');
    setDeleteOrg(null);
    await load(search);
  }

  const organizationColumns = useMemo<DataTableColumn<Organization>[]>(
    () => [
      {
        id: 'name',
        header: 'Organization',
        width: 240,
        minWidth: 140,
        sortable: true,
        sortValue: (org) => org.name,
        render: (org) => (
          <>
            <span className="workspace-switcher__icon" aria-hidden>
              {workspaceIcon({ workspaceType: org.workspaceType ?? 'organization' })}
            </span>{' '}
            {org.name}
          </>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: 140,
        minWidth: 100,
        sortable: true,
        sortValue: (org) => org.workspaceType ?? 'organization',
        render: (org) => org.workspaceType ?? 'organization',
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 280,
        minWidth: 220,
        resizable: false,
        actions: true,
        render: (org) => (
          <OrganizationRowActions
            org={org}
            onOpen={() => void openOrganization(org)}
            onEdit={() => setEditOrg(org)}
            onDelete={() => setDeleteOrg(org)}
          />
        ),
      },
    ],
    [],
  );

  if (!canManageOrganizations) {
    return <div className="panel alert alert-error">{error ?? 'Access denied.'}</div>;
  }

  return (
    <div>
      <PageHeader
        title="Organizations"
        subtitle="Create and manage customer workspaces globally — no need to leave Personal mode."
      />
      {error ? <div className="panel alert alert-error">{error}</div> : null}

      <section className="panel">
        <div className="table-action-bar">
          <input
            type="search"
            placeholder="Search organizations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search organizations"
          />
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            New organization
          </button>
        </div>

        {creating ? (
          <form className="workspace-create-form" onSubmit={createOrganization}>
            <label htmlFor="new-org-name">Organization name</label>
            <input
              id="new-org-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Acme Motors"
              required
            />
            <div className="table-action-bar">
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setCreating(false);
                  setSearchParams({});
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {loading ? <p className="muted">Loading organizations…</p> : null}
        {!loading && !items.length ? (
          <p className="muted">No organizations found.</p>
        ) : null}
        {!loading && items.length ? (
          <DataTable
            columns={organizationColumns}
            rows={items}
            getRowKey={(org) => org.id}
            stickyHeader={false}
          />
        ) : null}
      </section>

      {editOrg ? (
        <EditOrganizationModal
          organization={editOrg}
          onClose={() => setEditOrg(null)}
          onSave={saveOrganizationName}
        />
      ) : null}
      {deleteOrg ? (
        <DeleteOrganizationModal
          organization={deleteOrg}
          onClose={() => setDeleteOrg(null)}
          onConfirm={deleteOrganization}
        />
      ) : null}
    </div>
  );
}
